import { Lock, Check, X } from "lucide-react";

function TeamSide({ team, abbr, score, selected, winner, locked, disabled, onClick, side }) {
  const isWinner = winner === abbr;
  return (
    <button
      data-testid={`pick-${side}-${abbr}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`relative flex-1 flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-xl border transition-colors ${
        selected
          ? "border-[#D50A0A] bg-[#D50A0A]/10 neon-glow"
          : "border-[#1F1F22] bg-[#0A0A0A] hover:border-[#3a3a40]"
      } ${disabled ? "cursor-not-allowed" : "cursor-pointer active:scale-[0.98]"}`}
    >
      {selected && (
        <span className="absolute top-2 right-2 h-5 w-5 rounded-full bg-[#D50A0A] flex items-center justify-center">
          <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
        </span>
      )}
      <img
        src={team?.logo}
        alt={abbr}
        className={`h-12 w-12 object-contain transition-all ${
          winner && !isWinner ? "grayscale opacity-40" : ""
        }`}
        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
      />
      <span className="font-display font-bold text-sm text-white">{abbr}</span>
      <span className="text-[11px] text-zinc-500 leading-none">{team?.name}</span>
      {score !== null && score !== undefined && (
        <span className={`font-display text-2xl font-extrabold ${isWinner ? "neon-text" : "text-zinc-500"}`}>
          {score}
        </span>
      )}
    </button>
  );
}

export const GameCard = ({ game, pick, onPick, index = 0, showdownActive = false, showdownPick, onShowdownPick, showdownResult }) => {
  const locked = game.locked;
  const selected = pick?.selected_team;
  const kickoff = game.kickoff ? new Date(game.kickoff) : null;

  const statusLabel =
    game.status === "final" ? "FINAL"
    : game.status === "inprogress" ? "LIVE"
    : locked ? "LOCKED"
    : kickoff
      ? kickoff.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
      : "SCHEDULED";

  return (
    <div
      data-testid={`game-card-${game.away}-${game.home}`}
      style={{ animationDelay: `${index * 45}ms` }}
      className={`pp-fade-up rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-4 ${locked ? "opacity-90" : ""}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={`text-[11px] font-bold tracking-wide px-2 py-0.5 rounded ${
          game.status === "final" ? "bg-zinc-800 text-zinc-300"
          : game.status === "inprogress" ? "bg-red-500/15 text-red-400"
          : locked ? "bg-zinc-800 text-zinc-400"
          : "bg-[#D50A0A]/10 neon-text"
        }`}>
          {game.status === "inprogress" && <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500 mr-1 animate-pulse" />}
          {statusLabel}
        </span>
        <div className="flex items-center gap-2">
          {locked && <Lock className="h-3.5 w-3.5 text-zinc-500" />}
        </div>
      </div>

      <div className="flex items-stretch gap-2">
        <TeamSide
          side="away" team={game.away_team} abbr={game.away} score={game.away_score}
          selected={selected === game.away} winner={game.winner}
          locked={locked} disabled={locked} onClick={() => onPick(game.id, game.away)}
        />
        <div className="flex items-center justify-center px-1">
          <span className="font-display text-xs font-bold text-zinc-600">@</span>
        </div>
        <TeamSide
          side="home" team={game.home_team} abbr={game.home} score={game.home_score}
          selected={selected === game.home} winner={game.winner}
          locked={locked} disabled={locked} onClick={() => onPick(game.id, game.home)}
        />
      </div>

      {game.is_tiebreaker && (
        <div className="mt-3 pt-3 border-t border-[#1F1F22] text-[11px] text-zinc-500 flex items-center gap-1">
          <span className="overline !text-[10px]">Tiebreaker Game</span>
        </div>
      )}

      {showdownActive && (
        <div className="mt-3 pt-3 border-t border-amber-500/20 space-y-2" data-testid={`showdown-picks-${game.away}-${game.home}`}>
          {[
            { key: "passing_team", label: "Most Passing Yds", tid: "pass", winners: showdownResult?.passing_winners },
            { key: "rushing_team", label: "Most Rushing Yds", tid: "rush", winners: showdownResult?.rushing_winners },
          ].map((row) => {
            const isFinal = game.status === "final";
            const resolved = isFinal && showdownResult?.status === "resolved";
            const pending = isFinal && (!showdownResult || showdownResult.status !== "resolved");
            return (
              <div key={row.key}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-amber-300/80">{row.label}</div>
                  {pending && (
                    <span data-testid={`sd-${row.tid}-pending`} className="text-[9px] font-bold uppercase text-zinc-400 bg-zinc-800 px-1.5 py-0.5 rounded">Pending</span>
                  )}
                </div>
                <div className="flex gap-2">
                  {[game.away, game.home].map((abbr) => {
                    const sel = showdownPick?.[row.key] === abbr;
                    const isWinnerTeam = resolved && Array.isArray(row.winners) && row.winners.includes(abbr);
                    const correct = resolved && sel && isWinnerTeam;
                    const wrong = resolved && sel && !isWinnerTeam;
                    return (
                      <button key={abbr} data-testid={`sd-${row.tid}-${abbr}`} disabled={locked}
                        onClick={() => onShowdownPick(game.id, row.key, abbr)}
                        className={`relative flex-1 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                          correct ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
                          : wrong ? "border-red-500 bg-red-500/15 text-red-300"
                          : isWinnerTeam ? "border-emerald-500/50 bg-emerald-500/5 text-emerald-300/80"
                          : sel ? "border-amber-500 bg-amber-500/15 text-amber-300"
                          : "border-[#1F1F22] bg-[#0A0A0A] text-zinc-400 hover:border-amber-500/40"
                        } ${locked ? "cursor-not-allowed opacity-90" : "active:scale-[0.98]"}`}>
                        {correct && (
                          <span data-testid={`sd-${row.tid}-${abbr}-correct`}
                            className="absolute top-1 right-1 h-4 w-4 rounded-full bg-emerald-500 flex items-center justify-center">
                            <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                          </span>
                        )}
                        {wrong && (
                          <span data-testid={`sd-${row.tid}-${abbr}-wrong`}
                            className="absolute top-1 right-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center">
                            <X className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          {abbr}
                          {isWinnerTeam && !sel && (
                            <span data-testid={`sd-${row.tid}-${abbr}-result`} className="text-[8px] font-bold uppercase text-emerald-400">✓ Result</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
